import React, { useState, useRef, useEffect } from 'react';
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Componente MultiSelect profissional e moderno
 * - Tags com opções selecionadas
 * - Dropdown para selecionar/desselecionar opções
 * - Visual limpo e intuitivo
 * - Suporta múltiplas seleções
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value = [],
  onChange,
  placeholder = "Selecione as opções",
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown quando clica fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Toggle de seleção de uma opção
  const toggleOption = (optionValue: string) => {
    if (disabled) return;

    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    
    onChange(newValue);
  };

  // Remove uma opção específica (via tag)
  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter(v => v !== optionValue));
  };

  // Obtém o label de uma opção pelo value
  const getOptionLabel = (optionValue: string): string => {
    try {
      if (!optionValue || typeof optionValue !== 'string') {
        console.warn('[MultiSelectDropdown] optionValue inválido:', optionValue);
        return String(optionValue || 'Opção inválida');
      }
      
      const option = options.find(opt => opt?.value === optionValue);
      return option?.label || optionValue;
    } catch (error) {
      console.error('[MultiSelectDropdown] Erro ao obter label:', error);
      return String(optionValue || 'Erro');
    }
  };

  // Verifica se uma opção está selecionada
  const isSelected = (optionValue: string): boolean => {
    return value.includes(optionValue);
  };

  return (
    <div 
      ref={containerRef} 
      className={cn("relative w-full", className)}
    >
      {/* Campo de seleção principal */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          "min-h-[42px] w-full border border-gray-300 rounded-lg bg-white px-3 py-2 cursor-pointer transition-all",
          "hover:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500",
          disabled && "bg-gray-100 cursor-not-allowed opacity-60",
          isOpen && "ring-2 ring-blue-500 border-blue-500"
        )}
      >
        <div className="flex items-center justify-between gap-2">
          {/* Tags selecionadas ou placeholder */}
          <div className="flex-1 flex flex-wrap gap-1.5 min-h-[26px]">
            {value.length === 0 ? (
              <span className="text-gray-500 text-sm py-0.5">
                {placeholder}
              </span>
            ) : (
              value.map((selectedValue) => (
                <span
                  key={selectedValue}
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-500 text-white text-sm font-medium transition-all hover:bg-blue-600"
                >
                  <span className="max-w-[150px] truncate">
                    {getOptionLabel(selectedValue)}
                  </span>
                  <button
                    onClick={(e) => removeOption(selectedValue, e)}
                    className="hover:bg-blue-600 rounded-full p-0.5 transition-colors"
                    disabled={disabled}
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))
            )}
          </div>

          {/* Ícone de expansão */}
          <div className="flex-shrink-0">
            {isOpen ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </div>
        </div>
      </div>

      {/* Dropdown de opções */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              Nenhuma opção disponível
            </div>
          ) : (
            <ul className="py-1">
              {options
                .filter(option => {
                  // 🔧 VALIDAÇÃO: Garantir que option é válido antes de renderizar
                  if (!option || typeof option !== 'object') {
                    console.error('[MultiSelectDropdown] Opção inválida detectada:', option);
                    return false;
                  }
                  if (!option.value || typeof option.value !== 'string') {
                    console.error('[MultiSelectDropdown] Opção sem value válido:', option);
                    return false;
                  }
                  if (!option.label) {
                    console.warn('[MultiSelectDropdown] Opção sem label:', option);
                  }
                  return true;
                })
                .map((option) => {
                  try {
                    const selected = isSelected(option.value);
                    
                    return (
                      <li
                        key={option.value}
                        onClick={() => toggleOption(option.value)}
                        className={cn(
                          "px-3 py-2 cursor-pointer transition-colors flex items-center justify-between gap-2",
                          "hover:bg-blue-50",
                          selected && "bg-blue-50"
                        )}
                      >
                        <span className={cn(
                          "text-sm flex-1",
                          selected ? "text-blue-700 font-medium" : "text-gray-700"
                        )}>
                          {option.label || option.value}
                        </span>
                        
                        {selected && (
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        )}
                      </li>
                    );
                  } catch (error) {
                    console.error('[MultiSelectDropdown] Erro ao renderizar opção:', option, error);
                    return null;
                  }
                })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
